import React, { useState } from 'react';
import { useEmailComplianceChecker } from './useEmailComplianceChecker';
import './EmailComplianceChecker.css';

const EmailComplianceChecker = ({ emailText, subjectLine = '' }) => {
  const [expanded, setExpanded] = useState(false);

  const fullText = `${subjectLine}\n${emailText}`;

  const {
    violations,
    spamWarnings,
    complianceStatus,
    stats,
    recommendations,
    isAnalyzing,
    greenPatterns
  } = useEmailComplianceChecker(fullText);

  const redViolations = violations.filter(v => v.severity === 'high');
  const yellowViolations = violations.filter(v => v.severity === 'medium');
  const totalIssues = stats.totalViolations + stats.spamRisk;

  const getBadgeColor = () => {
    if (totalIssues === 0) return '#22c55e';
    if (stats.highRisk > 0) return '#ef4444';
    if (stats.mediumRisk > 0) return '#f59e0b';
    return '#8b5cf6';
  };

  const getBadgeLabel = () => {
    if (totalIssues === 0) return 'Compliant';
    if (stats.highRisk > 0) return `${totalIssues} Issues`;
    if (stats.mediumRisk > 0) return `${totalIssues} Warnings`;
    return `${stats.spamRisk} Spam`;
  };

  const getSummary = () => {
    if (totalIssues === 0 && greenPatterns.length > 0)
      return 'Your email uses neutral, legally safe wording — no compliance or spam issues detected.';
    if (totalIssues === 0)
      return 'No compliance or deliverability issues detected. Your email appears to be safe.';

    const parts = [];
    if (stats.highRisk > 0)
      parts.push(`${stats.highRisk} critical violation${stats.highRisk > 1 ? 's' : ''}`);
    if (stats.mediumRisk > 0)
      parts.push(`${stats.mediumRisk} warning${stats.mediumRisk > 1 ? 's' : ''}`);
    if (stats.spamRisk > 0)
      parts.push(`${stats.spamRisk} spam trigger${stats.spamRisk > 1 ? 's' : ''}`);
    return `${parts.join(', ')} found. Review the details below.`;
  };

  return (
    <div className="ecc-container">
      <div className="ecc-header" onClick={() => setExpanded(!expanded)}>
        <span className="ecc-icon"><i className="fa-solid fa-scale-balanced"></i></span>
        <span className="ecc-title">Compliance Check</span>
        <span className="ecc-badge" style={{ background: getBadgeColor() }}>
          {getBadgeLabel()}
        </span>
        {!expanded && totalIssues > 0 && (
          <span className="ecc-quick-stats">
            {stats.highRisk > 0 && <span className="ecc-quick-stat-red">🔴 {stats.highRisk}</span>}
            {stats.mediumRisk > 0 && <span className="ecc-quick-stat-yellow">🟡 {stats.mediumRisk}</span>}
            {stats.spamRisk > 0 && <span className="ecc-quick-stat-spam">📧 {stats.spamRisk}</span>}
          </span>
        )}
        <span className="ecc-chevron">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="ecc-body">
          <div className="ecc-summary">{getSummary()}</div>

          <div className="ecc-stats">
            <div className="ecc-stat">
              <div className="ecc-stat-header">
                <span className="ecc-stat-label">Critical</span>
              </div>
              <span className={`ecc-stat-value ${stats.highRisk > 0 ? 'ecc-stat-value--red' : 'ecc-stat-value--green'}`}>
                {stats.highRisk}
              </span>
            </div>
            <div className="ecc-stat">
              <div className="ecc-stat-header">
                <span className="ecc-stat-label">Warnings</span>
              </div>
              <span className={`ecc-stat-value ${stats.mediumRisk > 0 ? 'ecc-stat-value--yellow' : 'ecc-stat-value--green'}`}>
                {stats.mediumRisk}
              </span>
            </div>
            <div className="ecc-stat">
              <div className="ecc-stat-header">
                <span className="ecc-stat-label">Spam Risk</span>
              </div>
              <span className={`ecc-stat-value ${stats.spamRisk > 0 ? 'ecc-stat-value--spam' : 'ecc-stat-value--green'}`}>
                {stats.spamRisk}
              </span>
            </div>
            {stats.hasGreenPatterns && (
              <div className="ecc-stat">
                <div className="ecc-stat-header">
                  <span className="ecc-stat-label">Safe Phrases</span>
                </div>
                <span className="ecc-stat-value ecc-stat-value--green">{stats.greenPatternCount}</span>
              </div>
            )}
          </div>

          {redViolations.length > 0 && (
            <div className="ecc-section">
              <div className="ecc-section-header">
                <span className="ecc-section-icon">🚫</span>
                <div>
                  <div className="ecc-section-title">Critical Violations ({redViolations.length})</div>
                  <div className="ecc-section-desc">These must be fixed to ensure GDPR/UWG compliance.</div>
                </div>
              </div>
              <div className="ecc-violations-list">
                {redViolations.map((violation, index) => (
                  <div key={`red-${index}`} className="ecc-violation-item ecc-violation-item--red">
                    <div className="ecc-violation-head">
                      <span className="ecc-violation-label">{violation.label}</span>
                      <code className="ecc-violation-match">"{violation.matchedText}"</code>
                    </div>
                    <div className="ecc-violation-reason">{violation.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {yellowViolations.length > 0 && (
            <div className="ecc-section">
              <div className="ecc-section-header">
                <span className="ecc-section-icon">⚠️</span>
                <div>
                  <div className="ecc-section-title">Warnings ({yellowViolations.length})</div>
                  <div className="ecc-section-desc">Optional improvements for better legal safety.</div>
                </div>
              </div>
              <div className="ecc-violations-list">
                {yellowViolations.map((violation, index) => (
                  <div key={`yellow-${index}`} className="ecc-violation-item ecc-violation-item--yellow">
                    <div className="ecc-violation-head">
                      <span className="ecc-violation-label">{violation.label}</span>
                      <code className="ecc-violation-match">"{violation.matchedText}"</code>
                    </div>
                    <div className="ecc-violation-reason">{violation.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {spamWarnings.length > 0 && (
            <div className="ecc-section ecc-section--spam">
              <div className="ecc-section-header">
                <span className="ecc-section-icon">📧</span>
                <div>
                  <div className="ecc-section-title">Spam Triggers ({spamWarnings.length})</div>
                  <div className="ecc-section-desc">These words hurt inbox placement — email filters may flag your message.</div>
                </div>
              </div>
              <div className="ecc-violations-list">
                {spamWarnings.map((warning, index) => (
                  <div key={`spam-${index}`} className="ecc-violation-item ecc-violation-item--spam">
                    <div className="ecc-violation-head">
                      <span className="ecc-violation-label">{warning.label}</span>
                      <code className="ecc-violation-match">"{warning.matchedText}"</code>
                    </div>
                    <div className="ecc-violation-reason">{warning.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.length > 0 && (
            <div className="ecc-section">
              <div className="ecc-section-header">
                <span className="ecc-section-icon">💡</span>
                <div>
                  <div className="ecc-section-title">Recommendations</div>
                  <div className="ecc-section-desc">Suggestions for improved compliance.</div>
                </div>
              </div>
              <div className="ecc-recommendations">
                {recommendations.map((rec, index) => (
                  <div key={index} className="ecc-recommendation-item">{rec}</div>
                ))}
              </div>
            </div>
          )}

          {greenPatterns.length > 0 && violations.length === 0 && spamWarnings.length === 0 && (
            <div className="ecc-allclear">
              <span>✅</span>
              <span>All clear — your email uses neutral, legally safe wording without sales intent.</span>
            </div>
          )}

          <div className="ecc-legal">
            <p className="ecc-legal-basis">
              <strong>Legal Basis:</strong> BGH I ZR 78/20, OLG Hamburg 5 U 92/22,
              LG Berlin 52 O 128/23, OLG Köln 6 U 68/22, § 7 Abs. 2 Nr. 2 UWG
            </p>
            <p className="ecc-legal-disclaimer">
              ⚠️ <strong>Notice:</strong> This analysis serves solely as guidance and does not constitute legal advice.
              BonusClub assumes no liability for the accuracy, completeness, or legal assessment of the content.
              For binding legal information, please consult a lawyer.
            </p>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default EmailComplianceChecker;
